import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CreditCard, Gear, Users, Lightning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DataTable, FilterBar, FilterChip, StatusBadge, DetailDrawer, KPICard } from '../../components/admin/AdminComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [subs, setSubs] = useState([]);
  const [events, setEvents] = useState([]);
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('plans');
  const [subFilter, setSubFilter] = useState('');
  const [drawer, setDrawer] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/monetization/admin/plans`, { withCredentials: true }),
      axios.get(`${API_URL}/api/monetization/admin/subscriptions`, { withCredentials: true }),
      axios.get(`${API_URL}/api/monetization/admin/events`, { withCredentials: true }),
      axios.get(`${API_URL}/api/monetization/admin/usage`, { withCredentials: true }),
    ]).then(([p, s, e, u]) => {
      setPlans(p.data || []);
      setSubs(s.data || []);
      setEvents(e.data || []);
      setUsage(u.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const togglePlan = async (planCode, active) => {
    try {
      await axios.put(`${API_URL}/api/monetization/admin/plans/${planCode}`, { active: !active }, { withCredentials: true });
      toast.success(`Plan ${!active ? 'activated' : 'deactivated'}`);
      setPlans(plans.map(p => p.plan_code === planCode ? { ...p, active: !active } : p));
    } catch { toast.error('Failed'); }
  };

  const approvePublisher = async (pubId) => {
    try {
      await axios.post(`${API_URL}/api/monetization/admin/publisher/${pubId}/approve-invite`, {}, { withCredentials: true });
      toast.success('Publisher invite approved');
    } catch { toast.error('Failed'); }
  };

  const planCols = [
    {
      key: 'name', label: 'Plan',
      render: (_, row) => (
        <div>
          <p className="text-[13px] text-white font-medium">{row.name}</p>
          <p className="text-[10px] text-[#444] font-mono">{row.plan_code}</p>
        </div>
      ),
    },
    { key: 'tenant_type', label: 'Type', render: v => <StatusBadge status={v} size="xs" /> },
    { key: 'monthly_price', label: 'Price', render: v => <span className="text-[13px] text-white font-medium tabular-nums">${v}/mo</span> },
    { key: 'active', label: 'Status', render: v => <StatusBadge status={v ? 'active' : 'archived'} size="xs" /> },
    { key: 'invite_only', label: 'Invite Only', render: v => v ? <span className="text-[11px] text-[#F59E0B]">Yes</span> : <span className="text-[11px] text-[#333]">No</span> },
    {
      key: 'actions', label: '',
      render: (_, row) => (
        <button onClick={() => togglePlan(row.plan_code, row.active)}
          className={`text-[11px] px-2.5 py-1 rounded-md font-medium ${row.active ? 'bg-[#EF4444]/10 text-[#EF4444]' : 'bg-[#10B981]/10 text-[#10B981]'}`}
          data-testid={`toggle-plan-${row.plan_code}`}>
          {row.active ? 'Deactivate' : 'Activate'}
        </button>
      ),
    },
  ];

  const subCols = [
    { key: 'entity_name', label: 'Entity', render: v => <span className="text-[13px] text-white font-medium">{v || '—'}</span> },
    { key: 'tenant_type', label: 'Type', render: v => <StatusBadge status={v} size="xs" /> },
    { key: 'plan_code', label: 'Plan', render: v => <span className="text-[12px] text-[#888] font-mono">{v}</span> },
    { key: 'status', label: 'Status', render: v => <StatusBadge status={v} size="xs" /> },
    { key: 'started_at', label: 'Started', cellClass: 'hidden lg:table-cell', render: v => <span className="text-[11px] text-[#444]">{v ? new Date(v).toLocaleDateString() : '—'}</span> },
    { key: 'ends_at', label: 'Ends', cellClass: 'hidden lg:table-cell', render: v => <span className="text-[11px] text-[#444]">{v ? new Date(v).toLocaleDateString() : '—'}</span> },
  ];

  const eventCols = [
    { key: 'created_at', label: 'Date', render: v => <span className="text-[11px] text-[#555] tabular-nums">{v ? new Date(v).toLocaleString() : '—'}</span> },
    { key: 'event_type', label: 'Event', render: v => <StatusBadge status={v} size="xs" /> },
    { key: 'tenant_type', label: 'Type', render: v => <span className="text-[11px] text-[#666] capitalize">{v}</span> },
    { key: 'metadata', label: 'Details', render: v => <span className="text-[11px] text-[#555] font-mono">{JSON.stringify(v)}</span> },
  ];

  const filteredSubs = subFilter ? subs.filter(s => s.tenant_type === subFilter) : subs;

  return (
    <div data-testid="admin-plans-page">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Plans & Monetization</h1>
        <p className="text-[13px] text-[#444] mt-0.5">Manage subscriptions, entitlements, and billing</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <KPICard icon={CreditCard} label="Plans" value={plans.length} accent="#F59E0B" />
        <KPICard icon={Users} label="Subscriptions" value={subs.length} accent="#00F0FF" />
        <KPICard icon={Lightning} label="Events" value={events.length} accent="#9D4CDD" />
        <KPICard icon={Gear} label="Usage Records" value={usage.length} accent="#10B981" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-white/[0.06] pb-2">
        {['plans', 'subscriptions', 'events', 'usage'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-t-md text-[12px] font-medium capitalize transition-colors ${
              tab === t ? 'text-[#EF4444] bg-[#EF4444]/[0.06]' : 'text-[#555] hover:text-white'
            }`} data-testid={`tab-${t}`}>{t}</button>
        ))}
      </div>

      {tab === 'plans' && (
        <DataTable columns={planCols} data={plans} loading={loading} emptyMessage="No plans" rowTestIdPrefix="plan" />
      )}

      {tab === 'subscriptions' && (
        <>
          <div className="flex gap-2 mb-4">
            {['', 'player', 'shop', 'publisher'].map(f => (
              <FilterChip key={f} label={f || 'All'} active={subFilter === f} onClick={() => setSubFilter(f)} testId={`sub-filter-${f || 'all'}`} />
            ))}
          </div>
          <DataTable columns={subCols} data={filteredSubs} loading={loading} emptyMessage="No subscriptions" rowTestIdPrefix="sub" />
        </>
      )}

      {tab === 'events' && (
        <DataTable columns={eventCols} data={events} loading={loading} emptyMessage="No events yet" rowTestIdPrefix="event" />
      )}

      {tab === 'usage' && (
        <div className="space-y-2">
          {usage.length === 0 ? (
            <p className="text-[13px] text-[#444] text-center py-8">No usage data yet</p>
          ) : usage.map((u, i) => (
            <div key={i} className="flex items-center justify-between px-4 py-3 bg-[#0c0c14] border border-white/[0.06] rounded-lg">
              <div>
                <p className="text-[13px] text-white font-medium font-mono">{u.feature_code}</p>
                <p className="text-[10px] text-[#444] capitalize">{u.tenant_type}</p>
              </div>
              <div className="text-right">
                <p className="text-[14px] text-[#00F0FF] font-bold tabular-nums">{u.total_usage}</p>
                <p className="text-[10px] text-[#444]">{u.unique_users} users</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
